import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useUserData } from "features/User";

const grades = [
  { label: "Kindergarten", value: "K" },
  { label: "1st Grade", value: "1" },
  { label: "2nd Grade", value: "2" },
  { label: "3rd Grade", value: "3" },
  { label: "4th Grade", value: "4" },
  { label: "5th Grade", value: "5" },
  { label: "6th Grade", value: "6" },
  { label: "7th Grade", value: "7" },
  { label: "8th Grade", value: "8" },
  { label: "9th Grade", value: "9" },
  { label: "10th Grade", value: "10" },
  { label: "11th Grade", value: "11" },
  { label: "12th Grade", value: "12" },
  { label: "Educator", value: "Educator" },
];

export default function UserGradeLevelModal() {
  const {
    userCategory,
    graduationYear,
    isSubscriptionLoading,
    isAuthenticated,
    updateGradeLevel,
  } = useUserData();

  const [selectedGrade, setSelectedGrade] = useState("");
  const [saving, setSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [delayOpen, setDelayOpen] = useState(true);

  const shouldOpen = useMemo(() => {
    if (isSubscriptionLoading || !isAuthenticated) return false;

    const lowerCategory = userCategory?.toLowerCase();

    // Explicitly do not open for Higher Ed or Gov
    if (lowerCategory === "highered" || lowerCategory === "gov") {
      return false;
    }

    const isUserCategoryNull = userCategory == null;
    const isStudentWithNullGradYear = lowerCategory === "student" && graduationYear == null;

    return isUserCategoryNull || isStudentWithNullGradYear;

  }, [userCategory, graduationYear, isSubscriptionLoading, isAuthenticated]);

  async function handleUpdate() {
    if (!selectedGrade) return;
    setSaving(true);
    await updateGradeLevel(selectedGrade);
    setSaving(false);
  }

  useEffect(() => {
    setIsOpen(!delayOpen && shouldOpen);
    const timer = setTimeout(() => {
      if (isAuthenticated) {
        setDelayOpen(false);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [shouldOpen, delayOpen, isAuthenticated]);

  return (
    <Dialog open={isOpen} disableEscapeKeyDown>
      <DialogTitle>Update Your Grade Level</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <DialogContentText>
            Please select the grade you are in so that we can better tailor your
            experience to your grade level.
          </DialogContentText>
          <FormControl size="small" fullWidth>
            <InputLabel id="gradeLevelLabel">Select Grade</InputLabel>
            <Select
              labelId="gradeLevelLabel"
              label="Select Grade"
              value={selectedGrade}
              onChange={({ target }) => {
                setSelectedGrade(target.value);
              }}
            >
              {grades.map((grade) => (
                <MenuItem key={grade.value} value={grade.value}>
                  {grade.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: "secondary.main",
          px: 3,
          pb: 2
        }}
      >
        <LoadingButton
          onClick={handleUpdate}
          loading={saving}
          variant="contained"
          disabled={!selectedGrade}
          fullWidth
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
